import React, { useState, useMemo } from 'react';
import { X, Wand2, Copy, AlertTriangle, CheckCircle, Loader2, Code, ExternalLink, Clock, Sparkles, Target, TrendingUp, Shield, Info, Lock, Star, ArrowRight, AlertCircle } from 'lucide-react';
import { useGenerateFixes } from '../ai-fix/hooks/useGenerateFixes.ts';
import type { ToolTarget, FixResponse, FixResult, FixError } from '../ai-fix/engine/LLMProvider.ts';
import type { ScanFinding } from '../types/scan.ts';
import { SeverityChip } from './ui/SeverityChip.tsx';
import { getDeduplicationStats } from '../utils/findingUtils.ts';
import { useSub } from '../contexts/SubscriptionContext.tsx';

interface AIFixAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  scanId: string;
  findings: ScanFinding[];
  totalFindings: number;
}

const TOOL_OPTIONS: { value: ToolTarget; label: string; description: string; icon: string }[] = [
  { value: 'cursor', label: 'Cursor IDE', description: 'AI-powered code editor with context', icon: '⚡' },
  { value: 'bolt', label: 'Bolt.new', description: 'Web app builder with instant preview', icon: '🚀' },
  { value: 'claude', label: 'Claude', description: 'Comprehensive AI assistant', icon: '🧠' },
  { value: 'general', label: 'General', description: 'Universal AI tool compatible', icon: '🔧' }
];

// Enhanced fix quality metrics display
const FixQualityIndicator: React.FC<{ severity: string }> = ({ severity }) => {
  const getQualityInfo = (severity: string) => {
    switch (severity) {
      case 'critical':
        return { color: 'text-red-400', label: 'Urgent', icon: '🚨' };
      case 'high':
        return { color: 'text-orange-400', label: 'High Priority', icon: '⚠️' };
      case 'medium':
        return { color: 'text-yellow-400', label: 'Moderate', icon: '📋' };
      case 'low':
        return { color: 'text-blue-400', label: 'Low Priority', icon: 'ℹ️' };
      case 'info':
        return { color: 'text-gray-400', label: 'Info', icon: '💡' };
      default:
        return { color: 'text-gray-400', label: 'Info', icon: '💡' };
    }
  };

  const quality = getQualityInfo(severity);

  return (
    <div className={`flex items-center gap-1 text-xs ${quality.color}`}>
      <span>{quality.icon}</span>
      <span>{quality.label}</span>
    </div>
  );
};

// Complexity indicator component (no time estimates)
const ComplexityIndicator: React.FC<{ complexity?: string }> = ({ 
  complexity 
}) => {
  const getComplexityColor = (complexity?: string) => {
    switch (complexity) {
      case 'simple': return 'text-green-400';
      case 'complex': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  return (
    <div className="flex items-center gap-2 text-xs">
      {complexity && (
        <div className={`flex items-center gap-1 ${getComplexityColor(complexity)}`}>
          <div className={`w-2 h-2 rounded-full ${
            complexity === 'simple' ? 'bg-green-400' :
            complexity === 'complex' ? 'bg-red-400' : 'bg-yellow-400'
          }`} />
          <span className="capitalize">{complexity}</span>
        </div>
      )}
    </div>
  );
};

const AIFixAssistant: React.FC<AIFixAssistantProps> = ({
  isOpen,
  onClose,
  scanId,
  findings,
  totalFindings
}) => {
  const [selectedTool, setSelectedTool] = useState<ToolTarget>('cursor');
  const [generatedFixes, setGeneratedFixes] = useState<FixResponse | null>(null);
  const [copiedFixes, setCopiedFixes] = useState<Set<string>>(new Set());
  const [showCodeContext, setShowCodeContext] = useState(true);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [expandedFix, setExpandedFix] = useState<string | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(400); // Resizable width
  const [isCollapsed, setIsCollapsed] = useState(false); // Always unfolded by default
  const [showToolSelection, setShowToolSelection] = useState(true);
  const [showContextTable, setShowContextTable] = useState(false);

  const { generateFixes, isGenerating, error, clearError } = useGenerateFixes();
  const { data: subscription, canScan } = useSub();
  
  // More robust Pro check
  const isProUser = !!(subscription && subscription.plan === 'pro' && subscription.status === 'active');
  console.log('[AIFixAssistant] Subscription:', subscription, 'isProUser:', isProUser);

  // Check if user has AI fix privileges
  const hasAIFixPrivileges = subscription && subscription.ai_fixes_limit !== null && subscription.ai_fixes_limit > 0;
  const aiFixesRemaining = hasAIFixPrivileges 
    ? Math.max(0, (subscription.ai_fixes_limit || 0) - (subscription.ai_fixes_used || 0))
    : 0;

  console.log('[AIFixAssistant] AI Fix Status:', {
    hasAIFixPrivileges,
    aiFixesLimit: subscription?.ai_fixes_limit,
    aiFixesUsed: subscription?.ai_fixes_used,
    aiFixesRemaining
  });

  // Drag-to-resize logic
  const handleDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
      const newWidth = startWidth - (moveEvent.clientX - startX);
      setSidebarWidth(Math.max(320, Math.min(newWidth, 700)));
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // Group findings by severity for display with enhanced metrics
  const findingsBySeverity = useMemo(() => {
    const groups = {
      critical: findings.filter(f => f.severity === 'critical'),
      high: findings.filter(f => f.severity === 'high'),
      medium: findings.filter(f => f.severity === 'medium'),
      low: findings.filter(f => f.severity === 'low'),
      info: findings.filter(f => f.severity === 'info')
    };
    
    // Filter out empty groups and return in severity order
    return Object.entries(groups).filter(([_, groupFindings]) => groupFindings.length > 0);
  }, [findings]);

  // Calculate fix statistics (removed time calculations)
  const fixStats = useMemo(() => {
    if (!generatedFixes) return null;

    const complexityBreakdown = generatedFixes.fixes.reduce((acc, fix) => {
      const finding = findings.find(f => f.id === fix.finding_id);
      const complexity = finding?.fix_complexity || 'medium';
      acc[complexity] = (acc[complexity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      complexityBreakdown,
      autoFixableCount: generatedFixes.fixes.filter(fix => {
        const finding = findings.find(f => f.id === fix.finding_id);
        return finding?.auto_fixable;
      }).length
    };
  }, [generatedFixes, findings]);

  // Deduplication stats
  const dedupStats = getDeduplicationStats(findings);

  // Enhanced holistic prompt - engineering-focused and concise
  const holisticPrompt = useMemo(() => {
    if (!generatedFixes || !generatedFixes.fixes.length) return '';
    
    // Engineering-focused header with imperative tone (no time estimates)
    const summary = `// SafeVibe – Fix Pack ${generatedFixes.fixes.length}\n// Paste this into Cursor or your IDE to auto-fix your code.\n\n`;

    // Context table for quick scanning (collapsible)
    const contextTable = showContextTable ? `| Issue | File | Severity |\n|-------|------|----------|\n${generatedFixes.fixes.map((fix, i) => {
      const finding = findings.find(f => f.id === fix.finding_id);
      const fileName = fix.file_path.split('/').pop() || fix.file_path;
      const issueType = finding?.issue_type?.replace(/_/g, '-') || 'security-issue';
      const severity = finding?.severity?.toUpperCase() || 'MED';
      return `| ${i + 1}. ${issueType} | ${fileName}:${fix.line} | ${severity} |`;
    }).join('\n')}\n\n` : '';

    // Clean unified diff blocks only
    const diffBlocks = generatedFixes.fixes
      .map((fix, index) => {
        const finding = findings.find(f => f.id === fix.finding_id);
        const fileName = fix.file_path.split('/').pop() || fix.file_path;
        const issueType = finding?.issue_type?.replace(/_/g, '-') || 'security-issue';
        // Show file/line above each diff
        let header = `// ${index + 1}. ${issueType} | ${fileName}:${fix.line}`;
        // Show warning if patch_prompt is missing or not a valid diff
        if (!fix.patch_prompt || !fix.patch_prompt.startsWith('---')) {
          return `${header}\n[Manual review needed: No valid diff generated for this fix.]`;
        }
        return `${header}\n\n${fix.patch_prompt}`;
      })
      .join('\n\n');
    
    return summary + contextTable + diffBlocks;
  }, [generatedFixes, selectedTool, showContextTable, findings]);

  const handleGenerateFixes = async () => {
    if (!selectedTool) return;
    
    // Check if user has remaining AI fixes
    if (aiFixesRemaining === 0) {
      console.error('[AIFixAssistant] Cannot generate fixes: No remaining AI fixes quota');
      // You could set an error state here to show a user-friendly message
      return;
    }
    
    try {
      clearError();
      const result = await generateFixes({
        scanId,
        toolTarget: selectedTool
      });
      
      if (result) {
      setGeneratedFixes(result);
        console.log(`[AIFixAssistant] Generated ${result.fixes.length} fixes for ${selectedTool}`);
      }
    } catch (err) {
      console.error('[AIFixAssistant] Failed to generate fixes:', err);
    }
  };

  const handleCopyFix = async (fix: FixResult) => {
    if (!fix.patch_prompt) return;
    
    try {
      const content = selectedTool === 'cursor' 
        ? `// SafeVibe fix for ${fix.finding_id}\n\n${fix.patch_prompt}`
        : fix.patch_prompt;

      await (navigator as any).clipboard.writeText(content);
      
      setCopiedFixes(prev => {
        const newSet = new Set(prev);
        newSet.add(fix.finding_id);
        return newSet;
      });
      
      // Clear copied state after 2 seconds
      setTimeout(() => {
        setCopiedFixes(prev => {
          const newSet = new Set(prev);
          newSet.delete(fix.finding_id);
          return newSet;
        });
      }, 2000);
      
      console.log(`[AIFixAssistant] Copied fix for finding ${fix.finding_id}`);
    } catch (err) {
      console.error('[AIFixAssistant] Failed to copy fix:', err);
    }
  };

  // MERGED COPY FUNCTION - Single primary action
  const handleCopyFixPack = async () => {
    if (!generatedFixes || generatedFixes.fixes.length === 0) return;
    
    try {
      await (navigator as any).clipboard.writeText(holisticPrompt);
      
      // Mark all fixes as copied
      setCopiedFixes(new Set(generatedFixes.fixes.map(f => f.finding_id)));
      
      setTimeout(() => {
        setCopiedFixes(new Set());
      }, 2000);
      
      console.log(`[AIFixAssistant] Copied Fix Pack with ${generatedFixes.fixes.length} fixes for ${selectedTool}`);
    } catch (err) {
      console.error('[AIFixAssistant] Failed to copy Fix Pack:', err);
    }
  };

  const handleCopyPrompt = async () => {
    try {
      await (navigator as any).clipboard.writeText(holisticPrompt);
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2000);
    } catch (err) {
      console.error('[AIFixAssistant] Failed to copy holistic prompt:', err);
    }
  };

  const getFixForFinding = (findingId: string): FixResult | FixError | null => {
    if (!generatedFixes) return null;
    
    return generatedFixes.fixes.find(f => f.finding_id === findingId) || 
           generatedFixes.errors.find(e => e.finding_id === findingId) || 
           null;
  };

  if (!isOpen) return null;

  // Show spinner/loading state if generating and no fixes yet
  if (isGenerating && (!generatedFixes || !generatedFixes.fixes.length)) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
        <div className="text-blue-200 text-lg font-semibold mb-2">Generating AI Fixes...</div>
        <div className="text-gray-400 text-sm">This may take up to a minute for large scans. Please wait while we analyze your findings and generate step-by-step fixes.</div>
      </div>
    );
  }

  // Render Pro upgrade prompt for free users
  const renderProUpgradePrompt = () => (
    <div className="p-4 bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-lg">
      <div className="flex items-center gap-2 mb-3">
        <Lock className="w-4 h-4 text-purple-400" />
        <span className="text-purple-200 font-medium text-sm">Upgrade to Pro for AI Fixes</span>
      </div>
      
      <div className="space-y-3 mb-4">
        <div className="text-xs text-gray-300">✨ Preview: What you'll get</div>
        <div className="p-3 bg-gray-800/50 border border-gray-700 rounded">
          <div className="text-xs font-mono text-gray-400 mb-2">Fix for: Exposed API Key</div>
          <div className="text-xs text-gray-500 space-y-1">
            <div>1. Replace hardcoded key with environment variable</div>
            <div>2. Use process.env.API_KEY instead</div>
            <div>3. Add key to .env file and .gitignore</div>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-purple-400">
            <Lock className="w-3 h-3" />
            <span>Locked - Upgrade to View Full Fix</span>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <button className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2">
          <Star className="w-4 h-4" />
          Upgrade to Pro
          <ArrowRight className="w-4 h-4" />
        </button>
        <button className="w-full text-gray-400 hover:text-gray-300 text-xs py-1">
          Learn More
        </button>
      </div>

      <div className="text-xs text-gray-400">
        <div className="font-medium text-gray-300 mb-1">💡 Pro gets you:</div>
        <ul className="space-y-1 text-gray-500">
          <li>• AI-powered fix prompts</li>
          <li>• Code context & examples</li>
          <li>• Multiple tool targets</li>
          <li>• Priority support</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="flex h-full">
      {/* Drag handle for resizing */}
      <div
        style={{ cursor: 'ew-resize', width: 6, zIndex: 30 }}
        className="bg-gray-800 hover:bg-blue-700 transition-colors duration-150"
        onMouseDown={handleDrag}
        aria-label="Resize AI Fix Assistant sidebar"
      />
      {/* Resizable Sidebar */}
      <div 
        className={`flex-shrink-0 bg-gray-950 border-l border-gray-700 flex flex-col transition-all duration-300 ${
          isCollapsed ? 'w-12' : ''
        }`}
        style={{ width: isCollapsed ? '48px' : `${sidebarWidth}px` }}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-gray-700">
          <div className="flex items-center mb-4">
            <h2 className="text-2xl font-bold mb-2">AI Fix Assistant</h2>
          </div>
          {!isCollapsed && !isProUser && (
            <div className="flex flex-col gap-3 mb-4 p-4 bg-gradient-to-r from-blue-900 via-purple-900 to-blue-800 border-l-4 border-yellow-400 rounded shadow-lg">
              <div className="flex items-center gap-3 mb-2">
                <Lock className="w-6 h-6 text-yellow-400" />
                <span className="font-bold text-yellow-300">Pro Feature</span>
              </div>
              <div className="text-white font-semibold text-lg mb-1">Unlock AI-Powered Fixes</div>
              <div className="text-gray-200 mb-2">AI Fixes can save you hours. Upgrade to Pro for unlimited instant code fixes and advanced security guidance.</div>
              <ul className="text-sm text-gray-300 mb-2 list-disc list-inside">
                <li>AI-powered fix prompts for all findings</li>
                <li>Code context & actionable examples</li>
                <li>Multiple tool targets (Cursor, Bolt, Claude, etc.)</li>
                <li>Priority support & instant upgrades</li>
              </ul>
              <button
                onClick={() => window.location.href = '/upgrade'}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 font-bold py-2 px-4 rounded-lg shadow-lg transition-all text-base flex items-center justify-center gap-2"
              >
                <Star className="w-5 h-5" />
                Upgrade to Pro for AI Fixes
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isCollapsed ? (
            // Collapsed view - just icons
            <div className="p-2 space-y-2">
              <button
                onClick={() => setIsCollapsed(false)}
                className="w-full p-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs flex items-center justify-center"
                title="Expand AI Fix Assistant"
              >
                <Wand2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* 2. If not Pro, blur/lock all fix content and show upgrade CTA */}
              {!isProUser ? (
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-800/90 backdrop-blur-sm z-10 flex flex-col items-center justify-center">
                    <Lock className="w-10 h-10 text-yellow-400 animate-bounce mb-4" />
                  </div>
                  <div className="blur-sm select-none pointer-events-none">
                    {/* Render the normal fix content here, but blurred/locked */}
                    {generatedFixes && generatedFixes.fixes.length > 0 ? (
                      <pre className="text-xs text-gray-400 bg-gray-900 rounded p-4">{JSON.stringify(generatedFixes.fixes[0], null, 2)}</pre>
                    ) : (
                      <div className="text-gray-400 text-sm p-4">AI will generate step-by-step code fixes for your vulnerabilities here.</div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* 4. Add summary at the top */}
                  <div className="mb-4 text-blue-200 font-medium text-center">AI Fixes instantly generate step-by-step code changes for every vulnerability. Upgrade to Pro for unlimited access.</div>
                  {/* Holistic Prompt - move to top and emphasize after fixes are generated */}
                  {generatedFixes && generatedFixes.fixes.length > 0 && (
                    <div className="space-y-3 p-4 bg-blue-950/80 border-2 border-blue-700 rounded-lg shadow-lg mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-blue-400" />
                        <span className="text-blue-200 font-semibold text-base">Copy this prompt into your IDE or AI tool for step-by-step fixes</span>
                      </div>
                      <div className="flex items-center justify-end mb-2">
                        <label className="flex items-center gap-1 text-xs text-gray-400 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showContextTable}
                            onChange={(e) => setShowContextTable((e.target as HTMLInputElement).checked)}
                            className="form-checkbox h-3 w-3 rounded border-gray-500 text-blue-500 focus:ring-blue-500"
                          />
                          Context
                        </label>
                      </div>
                      <textarea
                        className="w-full bg-gray-950 border border-gray-800 rounded p-2 text-xs font-mono text-blue-100 resize-none"
                        rows={Math.min(holisticPrompt.split('\n').length + 1, 12)}
                        value={holisticPrompt}
                        readOnly
                        style={{ minHeight: 100 }}
                      />
                    </div>
                  )}
                  {/* Tool Selection - collapse/minimize after fixes are generated */}
                  {(!generatedFixes || !generatedFixes.fixes.length || showToolSelection) && (
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-gray-300">🎯 Choose your AI tool:</label>
                      <div className="grid grid-cols-1 gap-2">
                        {TOOL_OPTIONS.map((tool) => (
                          <button
                            key={tool.value}
                            onClick={() => setSelectedTool(tool.value)}
                            className={`p-3 rounded border text-left transition-colors ${
                              selectedTool === tool.value
                                ? 'border-blue-500 bg-blue-900/20 text-blue-300'
                                : 'border-gray-600 hover:border-gray-500 text-gray-400 hover:text-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{tool.icon}</span>
                              <span className="font-medium text-sm">{tool.label}</span>
                            </div>
                            <div className="text-xs opacity-75">{tool.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Show 'Change tool' button after fixes are generated */}
                  {generatedFixes && generatedFixes.fixes.length > 0 && !showToolSelection && (
                    <div className="mb-2 flex justify-end">
                      <button
                        className="text-xs text-blue-400 hover:underline"
                        onClick={() => setShowToolSelection(true)}
                      >
                        Change tool
                      </button>
                    </div>
                  )}
                  {/* Fix Statistics Preview - minimize after fixes are generated */}
                  {(!generatedFixes || !generatedFixes.fixes.length || showToolSelection) && fixStats && (
                    <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                      <div className="text-sm font-medium text-gray-300 mb-2">Fix Summary</div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="flex items-center gap-1">
                          <Target className="w-3 h-3 text-green-400" />
                          <span className="text-gray-400">Auto-fixable:</span>
                          <span className="text-white">{fixStats.autoFixableCount}</span>
                        </div>
                      </div>
                      <div className="mt-2 flex gap-2">
                        {Object.entries(fixStats.complexityBreakdown).map(([complexity, count]) => (
                          <div key={complexity} className="flex items-center gap-1 text-xs">
                            <div className={`w-2 h-2 rounded-full ${
                              complexity === 'simple' ? 'bg-green-400' :
                              complexity === 'complex' ? 'bg-red-400' : 'bg-yellow-400'
                            }`} />
                            <span className="text-gray-400">{complexity}:</span>
                            <span className="text-white">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Generate Button - hide after fixes are generated */}
                  {(!generatedFixes || !generatedFixes.fixes.length || showToolSelection) && (
                    <button
                      onClick={async () => {
                        await handleGenerateFixes();
                        setShowToolSelection(false);
                      }}
                      disabled={isGenerating || (!hasAIFixPrivileges && !isProUser) || aiFixesRemaining === 0}
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                        hasAIFixPrivileges && aiFixesRemaining > 0
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-600 text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      {isGenerating ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Generating enhanced fixes...</span>
                        </div>
                      ) : !hasAIFixPrivileges && !isProUser ? (
                        <div className="flex items-center justify-center gap-2">
                          <Shield className="h-4 w-4" />
                          <span>Upgrade to Pro for AI Fixes</span>
                        </div>
                      ) : aiFixesRemaining === 0 ? (
                        <div className="flex items-center justify-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          <span>AI Fixes Quota Exceeded</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <Sparkles className="h-4 w-4" />
                          <span>Generate AI Fixes ({aiFixesRemaining} left)</span>
                        </div>
                      )}
                    </button>
                  )}
                  {/* Generated Fixes */}
                  {isProUser && generatedFixes && (
                    <div className="space-y-4">
                      {/* SINGLE PRIMARY COPY BUTTON STRIP */}
                      <div className="bg-blue-600/10 border border-blue-500 rounded-xl p-4 flex justify-between items-center mb-4">
                        <div className="flex-1">
                          <div className="text-blue-700 font-bold text-lg mb-1">
                            ✅ {generatedFixes.fixes.length} fixes ready
                        </div>
                        {fixStats && (
                            <div className="flex gap-4 text-sm text-blue-800">
                              <span className="flex items-center gap-1">
                                ⚙️ {fixStats.autoFixableCount} auto-fixable
                              </span>
                              <span className="flex items-center gap-1">
                                ♟ {Object.keys(fixStats.complexityBreakdown).length} complexity levels
                              </span>
                            </div>
                          )}
                          </div>
                        
                        <div className="flex flex-col gap-2 items-end">
                        {generatedFixes.fixes.length > 0 && (
                            <button
                              onClick={handleCopyFixPack}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold text-base flex items-center gap-2 transition-colors shadow-lg"
                            >
                              📋 Copy {generatedFixes.fixes.length} Fixes for {selectedTool.charAt(0).toUpperCase() + selectedTool.slice(1)}
                            </button>
                          )}
                          <button
                            onClick={handleCopyPrompt}
                            className={`text-xs px-2 py-1 rounded transition-colors ${
                              copiedPrompt ? 'text-green-600' : 'text-blue-600 hover:text-blue-700'
                            }`}
                          >
                            {copiedPrompt ? '✓ Copied!' : 'Copy IDE prompt instead'}
                          </button>
                        </div>
                      </div>

                      {/* Enhanced Fixes by Severity */}
                      {findingsBySeverity.map(([severity, severityFindings]) => (
                        <div key={severity} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <SeverityChip severity={severity as any} size="sm" />
                              <span className="text-gray-400 text-xs">
                                {severityFindings.length} finding{severityFindings.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <FixQualityIndicator severity={severity} />
                          </div>

                          {severityFindings.slice(0, 3).map((finding) => { // Show max 3 per severity
                            const fixOrError = getFixForFinding(finding.id);
                            const isExpanded = expandedFix === finding.id;
                            
                            return (
                              <div key={finding.id} className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                                {/* Enhanced Finding Header */}
                                <div className="mb-2">
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="text-sm font-medium text-white truncate">
                                      {finding.issue_type}
                                    </div>
                                    <ComplexityIndicator 
                                      complexity={finding.fix_complexity} 
                                    />
                                  </div>
                                  <div className="text-xs text-gray-400 truncate mb-1">
                                    📁 {finding.file_path}:{finding.line}
                                  </div>
                                  
                                  {/* Auto-fixable indicator */}
                                  {finding.auto_fixable && (
                                    <div className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-900/20 rounded px-2 py-1">
                                      <CheckCircle className="w-3 h-3" />
                                      Auto-fixable
                                    </div>
                                  )}
                                </div>

                                {/* Fix or Error */}
                                {fixOrError && 'patch_prompt' in fixOrError ? (
                                  <div className="space-y-2">
                                    <div 
                                      className="p-2 bg-gray-900 rounded text-xs font-mono text-gray-300 cursor-pointer hover:bg-gray-850 transition-colors"
                                      onClick={() => setExpandedFix(isExpanded ? null : finding.id)}
                                    >
                                      <div className={`${isExpanded ? '' : 'max-h-16 overflow-hidden'}`}>
                                        {fixOrError.patch_prompt}
                                      </div>
                                      {!isExpanded && fixOrError.patch_prompt.length > 150 && (
                                        <div className="text-blue-400 text-xs mt-1">Click to expand...</div>
                                      )}
                                    </div>
                                    
                                    <button
                                      onClick={() => handleCopyFix(fixOrError)}
                                      className={`w-full flex items-center justify-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                                        copiedFixes.has(fixOrError.finding_id)
                                          ? 'bg-green-600 text-white'
                                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                                      }`}
                                    >
                                      {copiedFixes.has(fixOrError.finding_id) ? (
                                        <>
                                          <CheckCircle className="w-3 h-3" />
                                          Copied!
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="w-3 h-3" />
                                          Copy Fix
                                        </>
                                      )}
                                    </button>
                                  </div>
                                ) : fixOrError && 'reason' in fixOrError ? (
                                  <div className="text-xs text-red-400">
                                    ❌ {fixOrError.reason}
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-500 flex items-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Waiting for AI generation...
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          
                          {severityFindings.length > 3 && (
                            <div className="text-xs text-gray-500 text-center py-2">
                              +{severityFindings.length - 3} more {severity} finding{severityFindings.length - 3 !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No Findings State */}
                  {findings.length === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                      <div className="text-gray-300 font-medium mb-1">No Security Issues</div>
                      <div className="text-gray-500 text-sm">Your code looks secure!</div>
                    </div>
                  )}

                  {/* No Generated Fixes Yet */}
                  {!generatedFixes && !isGenerating && findings.length > 0 && isProUser && (
                    <div className="text-center py-8">
                      <Wand2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <div className="text-gray-400 font-medium mb-1">Ready to Generate Enhanced Fixes</div>
                      <div className="text-gray-500 text-sm">
                        Choose your tool and click "Generate AI Fixes" to get actionable security solutions
                      </div>
                    </div>
                  )}

                  {/* Error State - No fixes generated (potential file missing issue) */}
                  {generatedFixes && generatedFixes.fixes.length === 0 && !isGenerating && isProUser && (
                    <div className="text-center py-8">
                      <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                      <div className="text-gray-300 font-medium mb-1">No Fixes Generated</div>
                      <div className="text-gray-500 text-sm mb-4">
                        AI couldn't generate fixes for your findings. This may happen if:
                      </div>
                      <div className="text-left text-sm text-gray-400 space-y-1 mb-4 max-w-sm mx-auto">
                        <div>• Source files are missing from the scan</div>
                        <div>• Code context is insufficient</div>
                        <div>• Files were corrupted during upload</div>
                      </div>
                      <div className="text-xs text-blue-400">
                        Try re-uploading your project or contact support if the issue persists.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIFixAssistant; 